import { useEffect, useMemo, useState } from "react";
import { api, errText } from "../../lib/api";
import LocationPicker from "../../components/LocationPicker";
import { Lock, Info, Save, MapPin, Loader2, CheckCircle2 } from "lucide-react";

const DAYS = [
  ["mon", "Lundi"], ["tue", "Mardi"], ["wed", "Mercredi"], ["thu", "Jeudi"],
  ["fri", "Vendredi"], ["sat", "Samedi"], ["sun", "Dimanche"],
];

const STATUS_LABELS = {
  active: { label: "Actif", tone: "text-success" },
  onboarding: { label: "En cours d'intégration", tone: "text-warning" },
  paused: { label: "En pause", tone: "text-warning" },
  suspended: { label: "Suspendu", tone: "text-danger" },
  archived: { label: "Archivé", tone: "text-muted" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return iso; }
};

const ReadRow = ({ label, value, testid }) => (
  <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-3 py-2 border-b border-brass-light/60 last:border-0">
    <span className="text-xs text-muted uppercase tracking-wider sm:w-52 shrink-0">{label}</span>
    <span className="text-sm text-ink font-medium break-words" data-testid={testid}>{value || "—"}</span>
  </div>
);

const LegalPanel = ({ legal }) => {
  const st = STATUS_LABELS[legal.status] || { label: legal.status || "—", tone: "text-muted" };
  return (
    <div className="card p-5 space-y-2" data-testid="florist-profile-legal">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-lg text-ink flex items-center gap-2">
            <Lock size={16} className="text-brass" /> Informations légales
          </h3>
          <p className="text-xs text-muted">Contactez le support Fleurinelle pour toute correction (nom commercial, ICE, ville…).</p>
        </div>
        <span className={`smallcaps text-xs ${st.tone}`} data-testid="florist-profile-status">{st.label}</span>
      </div>
      <div className="mt-2">
        <ReadRow label="Nom commercial" value={legal.shop_name} testid="legal-shop-name" />
        <ReadRow label="Représentant légal" value={legal.owner_name} testid="legal-owner-name" />
        <ReadRow label="Numéro ICE" value={legal.ice_number} testid="legal-ice" />
        <ReadRow label="Ville" value={legal.city_name} testid="legal-city" />
        <ReadRow label="Années d'activité" value={legal.years_in_business ? `${legal.years_in_business} an${legal.years_in_business > 1 ? "s" : ""}` : "—"} testid="legal-years" />
        <ReadRow label="Vérifié le" value={fmtDate(legal.verified_at)} testid="legal-verified-at" />
      </div>
    </div>
  );
};

const OpeningHoursEditor = ({ value, onChange }) => (
  <div className="space-y-2" data-testid="opening-hours-editor">
    {DAYS.map(([k, label]) => {
      const d = value[k] || { open: "08:30", close: "20:00", closed: false };
      const upd = (patch) => onChange({ ...value, [k]: { ...d, ...patch } });
      return (
        <div key={k} className="flex flex-wrap items-center gap-3 py-1 border-b border-brass-light/50 last:border-0">
          <span className="w-24 text-sm text-ink">{label}</span>
          <label className="inline-flex items-center gap-2 text-xs text-muted">
            <input type="checkbox" data-testid={`hours-${k}-closed`} checked={d.closed}
                   onChange={(e) => upd({ closed: e.target.checked })} />
            Fermé
          </label>
          <div className={`flex items-center gap-2 ${d.closed ? "opacity-40 pointer-events-none" : ""}`}>
            <input type="time" data-testid={`hours-${k}-open`} value={d.open || "08:30"}
                   onChange={(e) => upd({ open: e.target.value })}
                   className="field !w-[100px] py-1 text-xs" />
            <span className="text-xs text-muted">→</span>
            <input type="time" data-testid={`hours-${k}-close`} value={d.close || "20:00"}
                   onChange={(e) => upd({ close: e.target.value })}
                   className="field !w-[100px] py-1 text-xs" />
          </div>
        </div>
      );
    })}
  </div>
);

const ContactAddressPanel = ({ contact, address, opening_hours, bio, onSaved }) => {
  const [form, setForm] = useState({
    phone: contact.phone || "",
    whatsapp: contact.whatsapp || "",
    website: contact.website || "",
    address: address.address || "",
    location: address.location || { lat: null, lng: null, place_id: "", formatted_address: "" },
    opening_hours: opening_hours || {},
    bio: { fr: bio.fr || "", en: bio.en || "", es: bio.es || "", ar: bio.ar || "" },
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify({
    phone: contact.phone || "", whatsapp: contact.whatsapp || "", website: contact.website || "",
    address: address.address || "",
    location: address.location || { lat: null, lng: null, place_id: "", formatted_address: "" },
    opening_hours: opening_hours || {},
    bio: { fr: bio.fr || "", en: bio.en || "", es: bio.es || "", ar: bio.ar || "" },
  }), [form, contact, address, opening_hours, bio]);

  const save = async () => {
    setErr(""); setOk(false); setBusy(true);
    try {
      const payload = {
        phone: form.phone, whatsapp: form.whatsapp, website: form.website,
        address: form.address,
        location: form.location?.lat != null ? {
          lat: form.location.lat, lng: form.location.lng,
          place_id: form.location.place_id || "",
          formatted_address: form.location.formatted_address || "",
        } : null,
        opening_hours: form.opening_hours,
        bio: form.bio,
      };
      const { data } = await api.patch("/florist/profile", payload);
      onSaved && onSaved(data);
      setOk(true);
      setTimeout(() => setOk(false), 2500);
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-5 space-y-5" data-testid="florist-profile-contact">
      <div>
        <h3 className="font-display text-lg text-ink">Coordonnées & boutique</h3>
        <p className="text-xs text-muted">Ces informations aident les livreurs et alimentent votre fiche publique.</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs text-muted uppercase tracking-wider">Téléphone</span>
          <input data-testid="profile-phone" className="field w-full mt-1" placeholder="+212 6XX XXX XXX"
                 value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </label>
        <label className="block">
          <span className="text-xs text-muted uppercase tracking-wider">WhatsApp</span>
          <input data-testid="profile-whatsapp" className="field w-full mt-1" placeholder="+212 6XX XXX XXX"
                 value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} />
        </label>
        <label className="block sm:col-span-2">
          <span className="text-xs text-muted uppercase tracking-wider">Site web (facultatif)</span>
          <input data-testid="profile-website" className="field w-full mt-1" placeholder="https://votre-boutique.ma"
                 value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
        </label>
      </div>

      <div className="space-y-2">
        <label className="block">
          <span className="text-xs text-muted uppercase tracking-wider">Adresse de la boutique</span>
          <textarea data-testid="profile-address" className="field w-full h-20 mt-1"
                    placeholder="N° et rue, quartier, code postal, ville"
                    value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
        </label>
        <div>
          <div className="flex items-center gap-2 text-xs text-muted uppercase tracking-wider mb-1">
            <MapPin size={12} /> Point de retrait sur la carte
          </div>
          <LocationPicker
            value={form.location?.lat != null ? form.location : null}
            cityHint={null}
            onChange={(v) => setForm((f) => ({ ...f, location: { lat: v.lat, lng: v.lng, place_id: v.place_id, formatted_address: v.formatted_address } }))}
          />
          <p className="text-[11px] text-muted mt-1">Déplacez la punaise à l'entrée exacte de votre boutique — les livreurs partenaires suivront ce point.</p>
        </div>
      </div>

      <div>
        <span className="text-xs text-muted uppercase tracking-wider">Horaires d'ouverture</span>
        <div className="mt-2">
          <OpeningHoursEditor value={form.opening_hours} onChange={(oh) => setForm({ ...form, opening_hours: oh })} />
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted uppercase tracking-wider">Bio publique (visible sur la vitrine)</span>
          <span className="text-[11px] text-muted">FR · EN</span>
        </div>
        <textarea data-testid="profile-bio-fr" className="field w-full h-20 mt-1"
                  placeholder="Présentation courte en français…"
                  value={form.bio.fr} onChange={(e) => setForm({ ...form, bio: { ...form.bio, fr: e.target.value } })} />
        <textarea data-testid="profile-bio-en" className="field w-full h-20"
                  placeholder="Short English presentation…"
                  value={form.bio.en} onChange={(e) => setForm({ ...form, bio: { ...form.bio, en: e.target.value } })} />
      </div>

      {err ? <p className="text-danger text-xs" data-testid="profile-error">{err}</p> : null}
      <div className="flex items-center gap-3">
        <button className="btn-primary inline-flex items-center gap-2" data-testid="profile-save"
                onClick={save} disabled={busy || !dirty}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Enregistrer
        </button>
        {ok ? <span className="text-success text-xs inline-flex items-center gap-1" data-testid="profile-saved">
          <CheckCircle2 size={14} /> Enregistré
        </span> : null}
        {!dirty && !ok ? <span className="text-xs text-muted">Aucune modification.</span> : null}
      </div>
    </div>
  );
};

const BankPanel = ({ bank, onSaved }) => {
  const [form, setForm] = useState({ bank_name: bank.bank_name || "", account_holder: bank.account_holder || "", rib: "" });
  const [attestation, setAttestation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const pending = bank.pending_bank_change;

  const pickAttestation = (file) => {
    setErr("");
    if (!file) return setAttestation(null);
    if (file.size > 8 * 1024 * 1024) { setErr("Fichier trop volumineux (8 Mo max)."); return; }
    const ct = (file.type || "").toLowerCase();
    if (!(ct === "image/jpeg" || ct === "image/jpg" || ct === "application/pdf")) {
      setErr("Format non accepté. Chargez un JPG ou un PDF."); return;
    }
    setAttestation(file);
  };

  const save = async () => {
    setErr(""); setOk(false); setBusy(true);
    try {
      if (!form.bank_name || !form.account_holder || !/^\d{24}$/.test(form.rib.replace(/\s/g, ""))) {
        throw new Error("Renseignez la banque, le titulaire et un RIB de 24 chiffres.");
      }
      if (!attestation) {
        throw new Error("Chargez l'attestation de RIB (JPG ou PDF) pour valider le changement.");
      }
      const fd = new FormData();
      fd.append("bank_name", form.bank_name);
      fd.append("account_holder", form.account_holder);
      fd.append("rib", form.rib.replace(/\s/g, ""));
      fd.append("attestation", attestation);
      await api.patch("/florist/bank", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, rib: "" });
      setAttestation(null);
      setOk(true);
      onSaved && onSaved();
      setTimeout(() => setOk(false), 3500);
    } catch (e) { setErr(errText(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="card p-5 space-y-3" data-testid="florist-profile-bank">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display text-lg text-ink">Coordonnées bancaires</h3>
          <p className="text-xs text-muted">Utilisées pour vos versements. Toute modification nécessite l'attestation de RIB de la banque et une validation admin.</p>
        </div>
        <span className={`smallcaps text-xs ${pending ? "text-warning" : bank.bank_verified ? "text-success" : "text-warning"}`} data-testid="bank-status">
          {pending ? "Changement en attente" : bank.bank_verified ? "Vérifié" : "En attente"}
        </span>
      </div>

      {pending ? (
        <div className="border border-warning/40 bg-warning/5 p-3 text-xs text-ink space-y-1" data-testid="bank-pending-change">
          <p className="font-medium">Un changement est en attente de validation par l'admin :</p>
          <p>Banque : <b>{pending.bank_name}</b> · Titulaire : <b>{pending.account_holder}</b> · Nouveau RIB : <b>••• {pending.rib_last4}</b></p>
          <p className="text-muted">Vos versements continuent d'aller sur l'ancien RIB jusqu'à approbation.</p>
        </div>
      ) : null}

      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs text-muted uppercase tracking-wider">Banque</span>
          <input data-testid="bank-name" className="field w-full mt-1" placeholder="Attijariwafa Bank, BMCE…"
                 value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} />
        </label>
        <label className="block">
          <span className="text-xs text-muted uppercase tracking-wider">Titulaire</span>
          <input data-testid="bank-holder" className="field w-full mt-1" placeholder="Nom du titulaire"
                 value={form.account_holder} onChange={(e) => setForm({ ...form, account_holder: e.target.value })} />
        </label>
        <label className="block sm:col-span-2">
          <span className="text-xs text-muted uppercase tracking-wider">
            Nouveau RIB (24 chiffres) {bank.rib_last4 ? <span className="text-muted normal-case tracking-normal">· actuel : ••• {bank.rib_last4}</span> : null}
          </span>
          <input data-testid="bank-rib" className="field w-full mt-1 tabular"
                 placeholder={bank.rib_last4 ? "Laisser vide pour ne pas changer" : "24 chiffres"}
                 inputMode="numeric" maxLength={30}
                 value={form.rib}
                 onChange={(e) => setForm({ ...form, rib: e.target.value.replace(/[^\d\s]/g, "") })} />
          <p className="text-[11px] text-muted mt-1 flex items-start gap-1">
            <Info size={11} className="mt-[2px]" /> Le RIB reste chiffré. Nous ne l'affichons jamais en clair, seulement les 4 derniers chiffres.
          </p>
        </label>
        <label className="block sm:col-span-2">
          <span className="text-xs text-muted uppercase tracking-wider">Attestation de RIB (JPG ou PDF, obligatoire)</span>
          <input type="file" accept="image/jpeg,application/pdf" data-testid="bank-attestation"
                 className="block mt-1 text-xs"
                 onChange={(e) => pickAttestation(e.target.files[0])} />
          {attestation ? (
            <p className="text-[11px] text-success mt-1">{attestation.name}</p>
          ) : (
            <p className="text-[11px] text-muted mt-1">Le document estampillé de votre banque, moins de 8 Mo.</p>
          )}
        </label>
      </div>

      {err ? <p className="text-danger text-xs" data-testid="bank-error">{err}</p> : null}
      <div className="flex items-center gap-3">
        <button className="btn-ghost inline-flex items-center gap-2" data-testid="bank-save"
                onClick={save} disabled={busy}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Soumettre le changement
        </button>
        {ok ? <span className="text-success text-xs inline-flex items-center gap-1" data-testid="bank-saved">
          <CheckCircle2 size={14} /> Envoyé — en attente de validation admin
        </span> : null}
      </div>
    </div>
  );
};

export default function AccountProfileCard() {
  const [data, setData] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const load = () => api.get("/florist/profile").then((r) => setData(r.data)).catch((e) => setLoadErr(errText(e)));
  useEffect(() => { load(); }, []);

  if (loadErr) return <p className="text-danger text-sm" data-testid="florist-profile-load-error">{loadErr}</p>;
  if (!data) return <p className="text-muted text-sm" data-testid="florist-profile-loading">Chargement du profil…</p>;

  return (
    <div className="space-y-4">
      <LegalPanel legal={data.legal} />
      <ContactAddressPanel
        contact={data.contact}
        address={data.address}
        opening_hours={data.opening_hours}
        bio={data.bio}
        onSaved={(fresh) => setData(fresh)}
      />
      <BankPanel bank={data.bank} onSaved={load} />
    </div>
  );
}
